import { useSearchParams } from "react-router-dom";
import { FlaskConical } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import useUserPermission from "~/hooks/useUserPermission";
import { AdminPage } from "~/pages/manifold-nextgen/components/admin/AdminPage";
import { VIEW_ALL_AI_CONVERSATIONS } from "~/router/permissions";
import { IndexTab } from "./IndexTab";
import { SearchTab } from "./SearchTab";

const TAB_PARAM = "tab";
const TABS = { SEARCH: "search", INDEX: "index" } as const;

/**
 * Semantic search, plus the index behind it for `view:all-ai-conversations`
 * holders, since its surveys name every user's projects and sessions.
 */
export const SearchManager = () => {
  useDocumentTitle("Search Manager");
  const canManageIndex = useUserPermission(VIEW_ALL_AI_CONVERSATIONS);
  // In the URL so a reload keeps the tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsIndex = searchParams.get(TAB_PARAM) === TABS.INDEX;
  const tab = wantsIndex && canManageIndex ? TABS.INDEX : TABS.SEARCH;

  return (
    <AdminPage
      title="Search Manager"
      description="Search everything you can see, and fill the index behind it"
      width="default"
    >
      <Alert variant="warning" className="mb-4" data-testid="search-manager-preview-banner">
        <FlaskConical />
        <AlertTitle>Under development</AlertTitle>
        <AlertDescription>
          <p>
            You can see this page because you&apos;re in the <code>ai-search-manager</code> flag.
            Results are only ever what you are authorized to see — there is no admin view over
            everyone&apos;s data here.
            {canManageIndex &&
              " Indexing and pruning write to this environment's shared index, so everyone on it sees the result."}
          </p>
        </AlertDescription>
      </Alert>

      <Tabs
        value={tab}
        onValueChange={(next) => setSearchParams({ [TAB_PARAM]: next }, { replace: true })}
      >
        <TabsList variant="underlined" className="mb-4">
          <TabsTrigger value={TABS.SEARCH}>Search</TabsTrigger>
          {canManageIndex && <TabsTrigger value={TABS.INDEX}>Index</TabsTrigger>}
        </TabsList>
        <TabsContent value={TABS.SEARCH}>
          <SearchTab />
        </TabsContent>
        {canManageIndex && (
          <TabsContent value={TABS.INDEX}>
            <IndexTab />
          </TabsContent>
        )}
      </Tabs>
    </AdminPage>
  );
};
